import dayjs from "dayjs";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CrHistoryPointDTO,
  TeamCrPointDTO,
} from "../../HubSpot/useGetSchedulerCloserDetail.ts";
import { formatRate } from "../Stats/closerStatsFormat.ts";

type ChartPoint = {
  day: string;
  label: string;
  cr: number | null;
  teamCr: number | null;
  won: number | null;
  callsDone: number | null;
  tier: string | null;
};

function toPercent(rate: number | null): number | null {
  return rate === null ? null : Math.round(rate * 1000) / 10;
}

function chartPoints(
  history: CrHistoryPointDTO[],
  team: TeamCrPointDTO[],
): ChartPoint[] {
  const byDay = new Map<string, ChartPoint>();
  const pointOn = (day: string): ChartPoint => {
    const existing = byDay.get(day);
    if (existing) return existing;
    const point: ChartPoint = {
      day,
      label: dayjs(day).format("DD/MM"),
      cr: null,
      teamCr: null,
      won: null,
      callsDone: null,
      tier: null,
    };
    byDay.set(day, point);
    return point;
  };
  for (const h of history) {
    const point = pointOn(h.day);
    point.cr = h.callsDone === 0 ? null : toPercent(h.cr);
    point.won = h.won;
    point.callsDone = h.callsDone;
    point.tier = h.tier;
  }
  for (const t of team) pointOn(t.day).teamCr = toPercent(t.cr);
  return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
}

function CrTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: ChartPoint }[];
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow">
      <p className="font-semibold">{dayjs(point.day).format("DD/MM/YYYY")}</p>
      <p>
        CR closer: {point.cr === null ? "—" : formatRate(point.cr / 100)}
        {point.callsDone !== null && ` (${point.won}/${point.callsDone})`}
      </p>
      {point.tier && <p>Tier: {point.tier}</p>}
      <p className="text-gray-500">
        CR team: {point.teamCr === null ? "—" : formatRate(point.teamCr / 100)}
      </p>
    </div>
  );
}

export function CloserCrHistoryChart({
  history,
  team,
}: {
  history: CrHistoryPointDTO[];
  team: TeamCrPointDTO[];
}) {
  const data = chartPoints(history, team);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} minTickGap={16} />
        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v: number) => `${v}%`} />
        <Tooltip content={<CrTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Line
          dataKey="cr"
          name="CR closer"
          stroke="#1E429F"
          strokeWidth={2}
          dot={false}
          connectNulls={false}
        />
        <Line
          dataKey="teamCr"
          name="CR team"
          stroke="#94A3B8"
          strokeWidth={2}
          strokeDasharray="5 4"
          dot={false}
          connectNulls={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
